import { useEffect, useState } from 'react';
import { useSvidQuery } from './useSvidQuery';

export const STELE_MIN_WIDTH = 2158;
/** Matches `--breakpoint-xs` (360px) in styles/index.css — true below that width. */
export const SMALL_MOBILE_MAX_WIDTH = 376;

/**
 * Subscribe to a CSS media query and re-render whenever it changes.
 * SSR-safe: falls back to `false` when `window` is unavailable.
 */
export const useMediaQuery = (query: string): boolean => {
    const [matches, setMatches] = useState(() => {
        if (typeof window === 'undefined') return false;
        return window.matchMedia(query).matches;
    });

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const media = window.matchMedia(query);
        const sync = () => setMatches(media.matches);

        sync();
        media.addEventListener('change', sync);
        return () => media.removeEventListener('change', sync);
    }, [query]);

    return matches;
};

export const useIsTouchDevice = (): boolean =>
    useMediaQuery('(pointer: coarse)');

export const useIsPortrait = (): boolean =>
    useMediaQuery('(orientation: portrait)');

export const useSteleBreakpoint = (): boolean => {
    const { isPresent: isSteleDevice } = useSvidQuery();
    const isWideViewport = useMediaQuery(`(min-width: ${STELE_MIN_WIDTH}px)`);
    const isTouchDevice = useIsTouchDevice();

    return isSteleDevice || (isWideViewport && !isTouchDevice);
};

/** Viewports narrower than 360px (Tailwind `max-xs`). */
export const useSmallMobileBreakpoint = (): boolean =>
    useMediaQuery(`(width < ${SMALL_MOBILE_MAX_WIDTH}px)`);

export const isIosSafari = (): boolean => {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent;
    if (/iPad|iPhone|iPod/.test(ua)) return true;
    return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
};